// PlaceSelector - Select a place for a new logbook entry

import React, { useState, useEffect, useMemo } from 'react';
import { Search, MapPin, Clock, X, ChevronRight } from 'lucide-react';
import type { Place, PlaceNote } from '../../types';

interface PlaceSelectorProps {
  places: Place[];
  recentNotes: PlaceNote[];
  onSelect: (place: { id: string; name: string; category?: string }) => void;
  onCancel: () => void;
}

export function PlaceSelector({
  places,
  recentNotes,
  onSelect,
  onCancel,
}: PlaceSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllPlaces, setShowAllPlaces] = useState(false);

  // Get unique recently visited places from notes
  const recentPlaces = useMemo(() => {
    const seen = new Set<string>();
    const result: { id: string; name: string; category?: string }[] = [];

    for (const note of recentNotes) {
      if (!seen.has(note.placeId)) {
        seen.add(note.placeId);
        result.push({
          id: note.placeId,
          name: note.placeName,
          category: note.placeCategory,
        });
      }
      if (result.length >= 10) break;
    }

    return result;
  }, [recentNotes]);

  // Filter places by search query
  const filteredPlaces = useMemo(() => {
    if (!searchQuery.trim()) {
      return showAllPlaces ? places.slice(0, 50) : [];
    }

    const query = searchQuery.toLowerCase();
    return places
      .filter(
        (place) =>
          place.name.toLowerCase().includes(query) ||
          place.address?.toLowerCase().includes(query) ||
          place.district?.toLowerCase().includes(query) ||
          place.city?.toLowerCase().includes(query)
      )
      .slice(0, 20);
  }, [places, searchQuery, showAllPlaces]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-h-[80vh] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Ort auswählen</h3>
        <button
          onClick={onCancel}
          className="p-1 hover:bg-gray-200 rounded transition"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Ort suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            autoFocus
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Recent Places */}
        {!searchQuery && recentPlaces.length > 0 && (
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Zuletzt besucht
            </h4>
            <div className="space-y-1">
              {recentPlaces.map((place) => (
                <button
                  key={place.id}
                  onClick={() => onSelect(place)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <div className="p-2 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                    <MapPin className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {place.name}
                    </p>
                    {place.category && (
                      <p className="text-sm text-gray-500 truncate">
                        {place.category}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchQuery && (
          <div className="p-4">
            {filteredPlaces.length > 0 ? (
              <>
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  Suchergebnisse
                </h4>
                <div className="space-y-1">
                  {filteredPlaces.map((place) => (
                    <button
                      key={place.id}
                      onClick={() =>
                        onSelect({
                          id: place.id,
                          name: place.name,
                          category: place.category,
                        })
                      }
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 rounded-lg transition-colors group"
                    >
                      <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-indigo-100 transition-colors">
                        <MapPin className="w-4 h-4 text-gray-600 group-hover:text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {place.name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {place.district || place.address}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Kein Ort gefunden für "{searchQuery}"</p>
                <p className="text-sm text-gray-400 mt-1">
                  Versuche einen anderen Suchbegriff
                </p>
              </div>
            )}
          </div>
        )}

        {/* Browse All */}
        {!searchQuery && (
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={() => setShowAllPlaces(!showAllPlaces)}
              className="w-full text-center text-sm text-indigo-600 hover:text-indigo-800 transition-colors py-2"
            >
              {showAllPlaces ? 'Weniger anzeigen' : 'Alle Orte durchsuchen'}
            </button>

            {showAllPlaces && (
              <div className="mt-3 space-y-1">
                {filteredPlaces.map((place) => (
                  <button
                    key={place.id}
                    onClick={() =>
                      onSelect({
                        id: place.id,
                        name: place.name,
                        category: place.category,
                      })
                    }
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <MapPin className="w-4 h-4 text-gray-400 group-hover:text-indigo-500" />
                    <span className="flex-1 truncate text-gray-700">
                      {place.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hint */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Wähle einen Ort aus, um einen neuen Logbuch-Eintrag zu erstellen
        </p>
      </div>
    </div>
  );
}

export default PlaceSelector;
